import React, { useState, useEffect } from "react";
import Navbar from "../components/Navbar";
import BottomNav from "../components/BottomNav";
import { useCart } from "../contexts/CartContext";
import { useAuth } from "../contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import "../styles/home.css";

import { State, City } from "country-state-city";

const API_URL = "https://ecommerce-backend-zxtw.onrender.com";

const Checkout = () => {
  const { cartItems, getTotalPrice, clearCart } = useCart();
  const { currentUser } = useAuth();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);

  const [paymentMethod, setPaymentMethod] = useState("prepaid");
  const [agreed, setAgreed] = useState(false);

  // 🔥 NEW STATES
  const [states, setStates] = useState([]);
  const [districts, setDistricts] = useState([]);

  const [formData, setFormData] = useState({
    name: "",
    phone: "",
    email: "",
    address1: "",
    address2: "",
    city: "",
    state: "",
    pincode: "",
    landmark: ""
  });

  // ================= LOAD STATES =================
  useEffect(() => {
    const indiaStates = State.getStatesOfCountry("IN");
    setStates(indiaStates);
  }, []);

  // ================= HANDLE INPUT =================
  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  // ================= STATE CHANGE =================
  const handleStateChange = (e) => {
    const stateCode = e.target.value;

    setFormData({
      ...formData,
      state: stateCode,
      city: ""
    });

    const cities = City.getCitiesOfState("IN", stateCode);
    setDistricts(cities);
  };

  // ================= VALIDATION =================
  const validateForm = () => {
    if (!formData.name) return "Enter your name";
    if (!formData.phone) return "Enter your phone number";
    if (!formData.address1) return "Enter address";
    if (!formData.state) return "Select state";
    if (!formData.city) return "Select district";
    if (!formData.pincode) return "Enter pincode";

    // ✅ PINCODE VALIDATION
    if (!/^[1-9][0-9]{5}$/.test(formData.pincode)) {
      return "Enter valid 6-digit PIN code";
    }

    if (!agreed) return "Please accept delivery condition";

    return null;
  };

  // ================= PLACE ORDER =================
  const placeOrder = async () => {

    if (cartItems.length === 0) {
      alert("Your cart is empty");
      return;
    }

    const error = validateForm();
    if (error) {
      alert(error);
      return;
    }

    if (!currentUser || !currentUser.uid) {
      alert("Please log in before placing an order.");
      navigate("/login");
      return;
    }

    if (paymentMethod === "cod") {
      alert("Cash on Delivery is currently unavailable.");
      return;
    }

    if (!window.Razorpay) {
      alert("Payment system not loaded. Refresh page.");
      return;
    }

    setLoading(true);

    try {
      await fetch(`${API_URL}/health`);
      await new Promise(resolve => setTimeout(resolve, 2000));

      const orderResponse = await fetch(`${API_URL}/api/payment/create-order`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          amount: getTotalPrice()
        })
      });

      const orderData = await orderResponse.json();

      if (!orderData.id) {
        alert("Order creation failed");
        setLoading(false);
        return;
      }

      const options = {
        key: process.env.REACT_APP_RAZORPAY_KEY,
        amount: orderData.amount,
        currency: "INR",
        name: "M&L Store",
        description: "Order Payment",
        order_id: orderData.id,

        handler: function (response) {
          const payload = {
            razorpay_order_id: response.razorpay_order_id,
            razorpay_payment_id: response.razorpay_payment_id,
            razorpay_signature: response.razorpay_signature,
            user: {
              ...formData,
              country: "India"
            },
            items: cartItems,
            total: getTotalPrice(),
            userId: currentUser?.uid || null
          };

          localStorage.setItem("pendingRazorpayVerification", JSON.stringify(payload));

          setLoading(false);

          navigate("/order-success", {
            state: {
              total: payload.total,
              paymentId: payload.razorpay_payment_id
            }
          });

          fetch(`${API_URL}/api/payment/verify-payment`, {
            method: "POST",
            headers: {
              "Content-Type": "application/json"
            },
            body: JSON.stringify(payload)
          })
            .then(res => res.json())
            .then(data => {
              if (data.success) {
                clearCart();
                localStorage.removeItem("pendingRazorpayVerification");
              }
            })
            .catch(() => {});
        },

        prefill: {
          name: formData.name,
          email: formData.email,
          contact: formData.phone
        },

        theme: {
          color: "#000"
        }
      };

      const rzp = new window.Razorpay(options);

      rzp.on("payment.failed", function (response) {
        setLoading(false);
        alert(response.error.description || "Payment Failed");
      });

      rzp.open();

    } catch (error) {
      setLoading(false);
      alert("Something went wrong");
    }
  };

  return (
    <div className="checkout-container">
      <Navbar />

      <h1 className="checkout-title">Checkout</h1>

      <div className="checkout-content">

        {/* ================= FORM ================= */}
        <div className="checkout-form">
          <h2>Delivery Information</h2>

          <input name="name" placeholder="Full Name" onChange={handleChange} />
          <input name="phone" placeholder="Phone Number" onChange={handleChange} />
          <input name="email" placeholder="Email Address" onChange={handleChange} />
          <input name="address1" placeholder="Address Line 1" onChange={handleChange} />
          <input name="address2" placeholder="Address Line 2" onChange={handleChange} />
          <input name="landmark" placeholder="Landmark" onChange={handleChange} />

          {/* ✅ COUNTRY */}
          <select disabled>
            <option>India</option>
          </select>

          {/* ✅ STATE */}
          <select name="state" onChange={handleStateChange}>
            <option value="">Select State</option>
            {states.map((state) => (
              <option key={state.isoCode} value={state.isoCode}>
                {state.name}
              </option>
            ))}
          </select>

          {/* ✅ DISTRICT */}
          <select name="city" value={formData.city} onChange={handleChange}>
            <option value="">Select District</option>
            {districts.map((district) => (
              <option key={district.name} value={district.name}>
                {district.name}
              </option>
            ))}
          </select>

          {/* ✅ PINCODE */}
          <input name="pincode" placeholder="PIN Code" onChange={handleChange} />

          {/* ================= PAYMENT OPTION ================= */}
              <h2 style={{ marginTop: "20px" }}>Payment Method</h2>

              <div
                    className={`payment-option ${paymentMethod === "prepaid" ? "active" : ""}`}
                    onClick={() => setPaymentMethod("prepaid")}
                  >
                <div className="radio-box">
                  <input
                    type="radio"
                    checked={paymentMethod === "prepaid"}
                    onChange={() => setPaymentMethod("prepaid")}
                  />
                </div>

                <div className="payment-text">
                  <div className="title">Prepaid (Online Payment)</div>
                </div>
              </div>

              <div className="payment-option disabled">
                <div className="radio-box">
                  <input type="radio" disabled />
                </div>

                <div className="payment-text">
                  <div className="title">Cash on Delivery (Unavailable)</div>
                  <p>
                    Cash on Delivery is currently unavailable due to a high number of unconfirmed orders.
                  </p>
                </div>
              </div>
            
            {/* ================= SUMMARY ================= */}
            <div className="checkout-summary">
              <h2>Order Summary</h2>

              {cartItems.map((item) => (
                <div key={item._id} className="summary-item">
                  <span>{item.name}</span>
                  <span>{item.quantity} × ₹{item.price}</span>
                </div>
              ))}

              {/* ================= PRICE TABLE ================= */}
              <div className="price-table">
                <div className="price-row">
                  <span>Product Amount</span>
                  <span>₹{getTotalPrice()}</span>
                </div>

                <div className="price-row">
                  <span>Delivery Charge</span>
                  <span>₹0</span>
                </div>

                <div className="price-row">
                  <span>GST</span>
                  <span>₹0</span>
                </div>

                <hr />

                <div className="price-row total">
                  <strong>Total</strong>
                  <strong>₹{getTotalPrice()}</strong>
                </div>
              </div>

              {/* ================= AGREEMENT ================= */}
              <div className="agreement-box">
                <p>
                  Orders must be collected from your nearest DTDC office. No delivery charges are applied.
                </p>

                <label>
                  <input
                    type="checkbox"
                    checked={agreed}
                    onChange={() => setAgreed(!agreed)}
                  />
                  I understand and agree
                </label>
              </div>

              <button onClick={placeOrder} disabled={loading}>
                {loading ? "Processing..." : "Place Order"}
              </button>
            </div>
        </div>

      </div>

      <BottomNav />
    </div>
  );
};

export default Checkout;